import type { DraftDataFilter, DataFilter } from "@/modules/widgets/types/dataFilters";
import { isDraftFilterComplete } from "@/modules/widgets/types/dataFilters";

export const CUSTOM_OPTION = "__custom__";

export interface ClarificationFilterFieldOption {
    field: string;
    label: string;
    kind: "string" | "number";
}

export interface ClarificationFilterPayload {
    filters: DraftDataFilter[];
    fieldOptions: ClarificationFilterFieldOption[];
}

export interface ClarificationItemShape {
    title: string;
    message: string;
    options: string[];
    optionValues?: string[];
    preselected: string[];
    multi: boolean;
    freeText?: boolean;
    uiType?: "select" | "list" | "chips";
    previewWidget?: Record<string, any>;
    filterPayload?: ClarificationFilterPayload;
}

export type ClarificationAnswer =
    | { title: string; values: string[] }
    | { title: string; action: "accept" | "reject"; filters?: DataFilter[] };

export interface ItemState {
    selections: string[];
    customText: string;
    filterDrafts: DraftDataFilter[];
    filterOpen: boolean;
}

export function initItemState(item: ClarificationItemShape): ItemState {
    return {
        selections: [...item.preselected],
        customText: "",
        filterDrafts: item.filterPayload?.filters.map((f) => ({ ...f })) ?? [],
        filterOpen: !!item.filterPayload,
    };
}

export function collectAnswer(item: ClarificationItemShape, state: ItemState): ClarificationAnswer {
    if (item.filterPayload) {
        const filters = state.filterDrafts.filter(isDraftFilterComplete) as DataFilter[];
        return { title: item.title, action: "accept", filters };
    }
    const { selections, customText } = state;
    if (selections.includes(CUSTOM_OPTION)) {
        const text = customText.trim();
        return { title: item.title, values: text ? [text] : [] };
    }
    if (item.freeText && !item.options.length) {
        return { title: item.title, values: [(selections[0] ?? "").trim()].filter(Boolean) };
    }
    const values = item.optionValues
        ? selections.map((label) => {
              const i = item.options.indexOf(label);
              return i >= 0 ? (item.optionValues![i] ?? label) : label;
          })
        : selections;
    return { title: item.title, values };
}

export function isItemBlocking(item: ClarificationItemShape, state: ItemState | undefined): boolean {
    if (!state) return true;
    if (item.filterPayload) {
        return state.filterDrafts.some((f) => !!f.error) || state.filterDrafts.some((f) => !isDraftFilterComplete(f));
    }
    if (item.freeText && !item.options.length) return (state.selections[0] ?? "").trim() === "";
    if (state.selections.includes(CUSTOM_OPTION)) return state.customText.trim() === "";
    if (!item.multi) return state.selections.length === 0;
    return false;
}
